import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import Header from '../components/Header';
import Footer from '../components/Footer';
import BackToTop from '../components/BackToTop';
import CustomCursor from '../components/CustomCursor';
import { ExternalLink, Scale, ShieldCheck, BookOpen } from 'lucide-react';
import { Head } from 'vite-react-ssg';

const Statutory = () => {
  const [darkMode, setDarkMode] = useState(false);
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.05 });

  const toggleDarkMode = () => {
    setDarkMode(!darkMode);
    document.documentElement.classList.toggle('dark');
  };

  const silk = [0.25, 0.46, 0.45, 0.94];
  const expo = [0.16, 1, 0.3, 1];

  const eyebrowV = { hidden:{opacity:0,y:14}, visible:{opacity:1,y:0,transition:{duration:0.7,ease:silk}} };
  const wordV    = { hidden:{opacity:0,y:44,rotateX:14}, visible:{opacity:1,y:0,rotateX:0,transition:{duration:0.8,ease:expo}} };
  const italicV  = { hidden:{opacity:0,x:-36,skewX:-5}, visible:{opacity:1,x:0,skewX:0,transition:{duration:0.7,ease:expo}} };
  const idxNumV  = { hidden:{opacity:0,y:10}, visible:{opacity:1,y:0,transition:{duration:0.9,ease:silk}} };
  const idxLineV = { hidden:{scaleX:0,opacity:0}, visible:{scaleX:1,opacity:1,transition:{duration:1.1,ease:expo}} };
  const cardV    = { hidden:{opacity:0,y:48,scale:0.97}, visible:{opacity:1,y:0,scale:1,transition:{duration:1.2,ease:expo}} };
  const footHrLV = { hidden:{scaleX:0,opacity:0}, visible:{scaleX:1,opacity:1,transition:{duration:1.2,ease:expo}} };
  const footHrRV = { hidden:{scaleX:0,opacity:0}, visible:{scaleX:1,opacity:1,transition:{duration:1.2,ease:expo}} };
  const footLblV = { hidden:{opacity:0,y:6}, visible:{opacity:1,y:0,transition:{duration:1.0,ease:silk,delay:0.2}} };

  const statutoryDocuments = [
    {
      title: 'Consumer Protection (Direct Selling) Rules, 2021',
      year: '2021',
      tag: 'Consumer Protection',
      icon: BookOpen,
      image: '/assest/direct_rule.jpeg',
      accentColor: '#E8734A',
      docs: [
        { name: 'View', path: '/Doc/Consumer Protection (Direct Selling) Rules, 2021.pdf' },
      ],
    },
    {
      title: 'Mizoram Registration for Direct Selling',
      year: '2025',
      tag: 'State Registration',
      icon: ShieldCheck,
      image: '/assest/Mizoram.jpg.jpeg',
      accentColor: '#4A9E6B',
      docs: [
        { name: 'View', path: '/Doc/Mizoram Consumer Protection DS.pdf' },
      ],
    },
    {
      title: 'Kerala High Court Order for Direct Selling',
      year: '2024',
      tag: 'Court Order',
      icon: Scale,
      image: '/assest/kerala_court.jpg',
      accentColor: '#4A7EC4',
      docs: [
        { name: 'Order 1', path: '/Doc/Kerala High Court order.pdf' },
        { name: 'Order 2', path: '/Doc/Kerala High Court order 1.pdf' },
        { name: 'Order 3', path: '/Doc/Kerala High Court order 2.pdf' },
      ],
    },
    {
      title: 'Telangana High Court Order for Direct Selling',
      year: '2024',
      tag: 'Court Order',
      icon: Scale,
      image: '/assest/telgana_court.jpg',
      accentColor: '#7A5CC4',
      docs: [
        { name: 'View', path: '/Doc/Business Continuity order.pdf' },
      ],
    },
  ];

  return (
    <div className={darkMode ? 'dark' : ''}> 

<Head>
  {/* ── Basic Meta ── */}
  <title>VibrantViva Statutory Information | Transparency and Compliance</title>
  <meta
    name="description"
    content="Find important statutory information, compliance updates, and transparency guidelines that reflect VibrantViva's commitment."
  />
  <meta
    name="keywords"
    content="VibrantViva Statutory Information, Transparency and Compliance, Legal Direct Selling, Direct Selling Company India, compliance guidelines, statutory disclosures, business transparency, legal network marketing company, transparency standards, compliance updates, regulatory information, direct selling regulations India"
  />
  <meta name="author" content="VibrantViva" />
  <meta name="robots" content="index, follow" />
  <link rel="canonical" href="https://vibrantviva.com/statutory" />

  {/* ── Open Graph (Facebook / WhatsApp / LinkedIn) ── */}
  <meta property="og:type" content="website" />
  <meta property="og:url" content="https://vibrantviva.com/statutory" />
  <meta
    property="og:title"
    content="VibrantViva Statutory Information | Transparency & Compliance"
  />
  <meta
    property="og:description"
    content="Find important statutory information, compliance updates, and transparency guidelines that reflect VibrantViva's commitment."
  />
  <meta property="og:image" content="https://vibrantviva.com/assest/logo.png" />
  <meta property="og:image:width" content="200" />
  <meta property="og:image:height" content="200" />
  <meta property="og:site_name" content="VibrantViva" />
  <meta property="og:locale" content="en_IN" />

  {/* ── JSON-LD Structured Data ── */}
  <script
  type="application/ld+json"
  dangerouslySetInnerHTML={{
    __html: JSON.stringify({
      "@context": "https://schema.org",
      "@type": "WebPage",
      "url": "https://vibrantviva.com/statutory",
      "name": "VibrantViva Statutory Information",
      "description":
        "Find important statutory information, compliance updates, and transparency guidelines that reflect VibrantViva's commitment.",
      "inLanguage": "en-IN",
      "isPartOf": {
        "@type": "WebSite",
        "name": "VibrantViva",
        "url": "https://vibrantviva.com/"
      },
      "publisher": {
        "@type": "Organization",
        "name": "VibrantViva",
        "url": "https://vibrantviva.com/",
        "logo": "https://vibrantviva.com/logo.png",
        "sameAs": [
          "https://www.facebook.com/media/set/?vanity=officialVibrantViva&set=a.705168821617957",
          "https://www.instagram.com/vibrantvivaofficial/",
          "https://www.linkedin.com/company/indusviva-healthsciences-private-limited/posts/?feedView=all&viewAsMember=true",
          "https://x.com/IndusvivaPvt?t=yf_P18W0HW6DR9ZbrhjRzg&s=09",
          "https://www.youtube.com/@VibrantViva"
        ],
        "contactPoint": {
          "@type": "ContactPoint",
          "telephone": "+91-1800-103-4916",
          "contactType": "Customer Support",
          "hoursAvailable": [
            {
              "@type": "OpeningHoursSpecification",
              "dayOfWeek": [
                "Monday",
                "Tuesday",
                "Wednesday",
                "Thursday",
                "Friday"
              ],
              "opens": "09:30",
              "closes": "18:00"
            },
            {
              "@type": "OpeningHoursSpecification",
              "dayOfWeek": ["Saturday"],
              "opens": "09:30",
              "closes": "13:30"
            }
          ],
          "availableLanguage": [
            "English",
            "Hindi",
            "Kannada",
            "Tamil",
            "Telugu",
            "Malayalam"
          ],
          "areaServed": "IN",
          "contactOption": "TollFree"
        },
        "address": {
          "@type": "PostalAddress",
          "streetAddress": "No 92-1-36, Viva Tower, Nandi Durga Road, Jayamahal Extension",
          "addressLocality": "Bangalore Urban",
          "addressRegion": "Karnataka",
          "postalCode": "560046",
          "addressCountry": "IN"
        },
        "location": {
          "@type": "Place",
          "name": "Viva Tower",
          "address": {
            "@type": "PostalAddress",
            "streetAddress": "No 92-1-36, Viva Tower, Nandi Durga Road, Jayamahal Extension",
            "addressLocality": "Bangalore Urban",
            "addressRegion": "Karnataka",
            "postalCode": "560046",
            "addressCountry": "IN"
          }
        }
      }
    })}
  }/>
</Head>

      <style dangerouslySetInnerHTML={{ __html: `
        @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,400;0,500;0,600;1,400;1,500&family=Outfit:wght@300;400;500;600&display=swap');
        * { box-sizing: border-box; }

        .stat-root { font-family: 'Outfit', sans-serif; background: #F8F6F2; min-height: 100vh; }

        .stat-hero-banner { background: #1a2e1a; padding-bottom: 52px; }

        .stat-eyebrow {
          font-family: 'Outfit', sans-serif; font-size: 0.6rem; font-weight: 500;
          letter-spacing: 0.3em; text-transform: uppercase; color: #8fbb8f;
          display: flex; align-items: center; gap: 12px;
        }
        .eyebrow-line { display: inline-block; width: 32px; height: 1px; background: #4CAF50; }

        .stat-heading {
          font-family: 'Cormorant Garamond', serif; font-weight: 500;
          letter-spacing: -0.02em; line-height: 1.1; color: #ffffff; perspective: 700px;
        }
        .stat-heading-italic {
          font-family: 'Cormorant Garamond', serif; font-style: italic;
          font-weight: 400; color: #4CAF50; display: block; margin-top: 4px;
        }
        .stat-desc {
          font-family: 'Outfit', sans-serif; font-size: 0.82rem;
          color: rgba(220,240,220,0.7); font-weight: 300; line-height: 1.75; max-width: 380px;
        }
        .section-divider {
          height: 1px; background: linear-gradient(to right, rgba(76,175,80,0.5), transparent);
          margin-top: 20px;
        }

        .stat-cards-section { background: #F8F6F2; padding: 52px 0 80px; }

        .doc-card {
          background: #FFFFFF; border-radius: 12px; overflow: hidden;
          border: 1px solid #EDE8E0;
          transition: border-color 0.4s ease, box-shadow 0.4s ease, transform 0.4s ease;
          display: flex; flex-direction: column;
        }
        .doc-card:hover {
          border-color: #4CAF50;
          box-shadow: 0 20px 48px rgba(76,175,80,0.12);
          transform: translateY(-4px);
        }

        .doc-card-img { position: relative; height: 300px; overflow: hidden; background: #F0EBE2; }
        .doc-card-img img {
          width: 100%; height: 100%; object-fit: cover; object-position: center center;
          transition: transform 2.5s cubic-bezier(0.16,1,0.3,1), filter 0.6s ease;
          filter: brightness(0.95) saturate(0.9);
        }
        .doc-card:hover .doc-card-img img { transform: scale(1.05); filter: brightness(0.88) saturate(1.0); }

        .doc-placeholder {
          width: 100%; height: 100%; display: flex; align-items: center; justify-content: center;
          background: linear-gradient(135deg, #f0f7f0 0%, #e4f0e4 100%);
          position: relative; overflow: hidden;
        }
        .placeholder-grid {
          position: absolute; inset: 0;
          background-image: radial-gradient(rgba(76,175,80,0.08) 1px, transparent 1px);
          background-size: 20px 20px;
        }

        .img-overlay {
          position: absolute; inset: 0;
          background: linear-gradient(to bottom, transparent 40%, rgba(0,0,0,0.35) 100%);
        }
        .img-tag {
          position: absolute; top: 14px; left: 14px;
          font-family: 'Outfit', sans-serif; font-size: 0.58rem; font-weight: 500;
          letter-spacing: 0.18em; text-transform: uppercase;
          padding: 4px 10px; border-radius: 100px;
          border: 1px solid rgba(255,255,255,0.35);
          background: rgba(255,255,255,0.15); backdrop-filter: blur(8px); color: #fff;
        }
        .img-tag-dark {
          border-color: rgba(76,175,80,0.35);
          background: rgba(76,175,80,0.12); color: #2e7d32;
        }
        .img-year {
          position: absolute; top: 14px; right: 14px;
          font-family: 'Cormorant Garamond', serif; font-style: italic;
          font-size: 0.85rem; color: rgba(255,255,255,0.8);
        }
        .img-year-dark { color: rgba(46,125,50,0.7); }

        .accent-bar { position: absolute; top: 0; left: 0; right: 0; height: 3px; }

        .doc-card-body { padding: 22px; flex: 1; display: flex; flex-direction: column; gap: 14px; }
        .doc-title {
          font-family: 'Cormorant Garamond', serif; font-weight: 500;
          font-size: 1.18rem; line-height: 1.35; color: #1a2e1a; letter-spacing: -0.01em;
        }
        .doc-sep { height: 1px; background: #EDE8E0; }

        .doc-actions {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-top: auto;
          gap: 12px;
          flex-wrap: nowrap;
        }
        .doc-actions-label {
          font-family: 'Outfit', sans-serif; font-size: 0.6rem; font-weight: 400;
          letter-spacing: 0.22em; text-transform: uppercase; color: #aab8aa;
          flex-shrink: 0;
        }

        /* ── Multi-doc button list — single row ── */
        .doc-btn-list {
          display: flex;
          flex-direction: row;
          flex-wrap: nowrap;
          gap: 6px;
          align-items: center;
          justify-content: flex-end;
        }

        .btn-view {
          display: inline-flex; align-items: center; gap: 6px;
          padding: 8px 12px; border-radius: 100px;
          font-family: 'Outfit', sans-serif; font-size: 0.58rem; font-weight: 500;
          letter-spacing: 0.15em; text-transform: uppercase;
          text-decoration: none; color: #1a2e1a; background: transparent;
          border: 1px solid #c8ddc8;
          transition: background 0.3s ease, border-color 0.3s ease, color 0.3s ease, transform 0.3s ease, box-shadow 0.3s ease;
          white-space: nowrap;
        }
        .btn-view:hover {
          background: #1a2e1a; border-color: #1a2e1a; color: #fff;
          transform: translateY(-1px); box-shadow: 0 6px 20px rgba(26,46,26,0.18);
        }
        .btn-view-arrow {
          width: 18px; height: 18px; border-radius: 50%;
          background: #e8f0e8; border: 1px solid #c8ddc8;
          display: flex; align-items: center; justify-content: center;
          transition: background 0.3s ease, border-color 0.3s ease, transform 0.4s cubic-bezier(0.34,1.56,0.64,1);
          flex-shrink: 0;
        }
        .btn-view:hover .btn-view-arrow {
          background: rgba(255,255,255,0.15); border-color: rgba(255,255,255,0.3);
          transform: rotate(45deg);
        }

        .index-num {
          font-family: 'Cormorant Garamond', serif; font-style: italic;
          font-size: 0.82rem; color: #4CAF50;
        }
        .index-hr { flex: 1; height: 1px; background: rgba(76,175,80,0.2); transform-origin: left center; }

        .foot-hr { flex: 1; height: 1px; background: #DDE8DD; }
        .foot-hr-left  { transform-origin: left center; }
        .foot-hr-right { transform-origin: right center; }
        .foot-label {
          font-family: 'Outfit', sans-serif; font-size: 0.55rem;
          letter-spacing: 0.3em; text-transform: uppercase; color: #7a9e7a;
        }
      `}} />

      <CustomCursor />

      <div className="stat-root">

        {/* ── Dark green hero ── */}
        <div className="stat-hero-banner">
          <Header darkMode={darkMode} toggleDarkMode={toggleDarkMode} />

          <div ref={ref} className="container mx-auto px-6 max-w-6xl pt-32">

            <motion.p
              className="stat-eyebrow mb-5"
              variants={eyebrowV} initial="hidden" animate={inView ? 'visible' : 'hidden'}
            >
              <span className="eyebrow-line" />
              Legal & Compliance
            </motion.p>

            <div className="flex items-end gap-7 mb-4">
              <motion.h1
                className="stat-heading text-4xl md:text-5xl"
                variants={{ hidden:{}, visible:{ transition:{ staggerChildren:0.12, delayChildren:0.18 } } }}
                initial="hidden" animate={inView ? 'visible' : 'hidden'}
              >
                <span style={{ display:'flex', flexWrap:'wrap', gap:'0 0.3em' }}>
                  {['Statutory'].map((w) => (
                    <motion.span key={w} variants={wordV} style={{ display:'inline-block' }}>{w}</motion.span>
                  ))}
                </span>
                <motion.span className="stat-heading-italic" variants={italicV}>Documents</motion.span>
              </motion.h1>

              <motion.div
                style={{ width:1, alignSelf:'stretch', background:'linear-gradient(to bottom, transparent, rgba(76,175,80,0.5), transparent)', marginBottom:4 }}
                className="hidden md:block"
                initial={{ opacity:0, scaleY:0.5 }}
                animate={inView ? { opacity:1, scaleY:1 } : {}}
                transition={{ duration:0.7, ease:expo }}
              />

              <motion.p
                className="stat-desc hidden md:block"
                style={{ marginBottom:4 }}
                variants={eyebrowV} initial="hidden" animate={inView ? 'visible' : 'hidden'}
              >
                Access our statutory documents, court orders, and regulatory compliance records for complete transparency and verification.
              </motion.p>
              <motion.p
                className="stat-desc md:hidden"
                style={{ marginBottom:4 }}
                variants={eyebrowV} initial="hidden" animate={inView ? 'visible' : 'hidden'}
              >
                Access our statutory documents, court orders, and regulatory compliance records for complete transparency and verification.
              </motion.p>
            </div>

            <motion.div
              className="section-divider"
              initial={{ opacity:0, scaleX:0.5 }}
              animate={inView ? { opacity:1, scaleX:1 } : {}}
              transition={{ duration:0.7, ease:expo }}
            />
          </div>
        </div>

        {/* ── Light cards section ── */}
        <div className="stat-cards-section">
          <div className="container mx-auto px-6 max-w-6xl">
            <motion.div
              className="grid grid-cols-1 md:grid-cols-2 gap-6"
              variants={{ hidden:{}, visible:{ transition:{ staggerChildren:0.15, delayChildren:0.4 } } }}
              initial="hidden" animate={inView ? 'visible' : 'hidden'}
            >
              {statutoryDocuments.map((item, index) => {
                const Icon = item.icon;
                const hasImage = !!item.image;
                const multiDoc = item.docs.length > 1;
                return (
                  <motion.div key={index} variants={cardV}>
                    <motion.div
                      className="flex items-center gap-3 mb-3"
                      variants={{ hidden:{}, visible:{ transition:{ staggerChildren:0.1 } } }}
                    >
                      <motion.span className="index-num" variants={idxNumV}>0{index + 1}</motion.span>
                      <motion.div className="index-hr" variants={idxLineV} />
                    </motion.div>

                    <div className="doc-card">
                      <div className="accent-bar" style={{ background: item.accentColor }} />
                      <div className="doc-card-img">
                        {hasImage ? (
                          <>
                            <img src={item.image} alt={item.title} />
                            <div className="img-overlay" />
                            <span className="img-tag">{item.tag}</span>
                            <span className="img-year">{item.year}</span>
                          </>
                        ) : (
                          <div className="doc-placeholder">
                            <div className="placeholder-grid" />
                            <Icon size={52} strokeWidth={1} style={{ color: item.accentColor, opacity: 0.4, position:'relative', zIndex:1 }} />
                            <span className="img-tag img-tag-dark">{item.tag}</span>
                            <span className="img-year img-year-dark">{item.year}</span>
                          </div>
                        )}
                      </div>

                      <div className="doc-card-body">
                        <h3 className="doc-title">{item.title}</h3>
                        <div className="doc-sep" />

                        <div className="doc-actions">
                          <span className="doc-actions-label">
                            {multiDoc ? 'Open Documents' : 'Open Document'}
                          </span>

                          <div className="doc-btn-list">
                            {item.docs.map((doc, di) => (
                              <a
                                key={di}
                                href={doc.path}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="btn-view"
                              >
                                {doc.name}
                                <span className="btn-view-arrow">
                                  <ExternalLink size={9} strokeWidth={2} />
                                </span>
                              </a>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </motion.div>

            <motion.div
              className="mt-14 flex items-center gap-4"
              variants={{ hidden:{}, visible:{ transition:{ staggerChildren:0.08, delayChildren:1.2 } } }}
              initial="hidden" animate={inView ? 'visible' : 'hidden'}
            >
              <motion.div className="foot-hr foot-hr-left" variants={footHrLV} />
              <motion.span className="foot-label" variants={footLblV}>Statutory & Compliance</motion.span>
              <motion.div className="foot-hr foot-hr-right" variants={footHrRV} />
            </motion.div>
          </div>
        </div>

      </div>

      <Footer />
      <BackToTop />
    </div>
  );
};

export default Statutory;